import React, { useState } from 'react';
import { StarIcon, UserIcon } from 'lucide-react';
import { Review } from '../data/products';
interface ReviewSectionProps {
  reviews: Review[];
  averageRating: number;
  totalReviews: number;
}
export function ReviewSection({
  reviews,
  averageRating,
  totalReviews
}: ReviewSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  // Calculate rating distribution
  const distribution = [5, 4, 3, 2, 1].map(star => {
    const count = reviews.filter(r => Math.round(r.rating) === star).length;
    const percentage = totalReviews > 0 ? Math.round(count / totalReviews * 100) : 0;
    return {
      star,
      count,
      percentage
    };
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would call an API
    alert('Thank you for your review! It will be published after moderation.');
    setShowForm(false);
    setComment('');
    setRating(5);
  };
  return <div className="py-8" data-id="element-263">
      <h3 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-264">
        Customer Reviews
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12" data-id="element-265">
        {/* Summary */}
        <div className="md:col-span-4 flex flex-col items-center justify-center p-6 bg-slate-50 rounded-2xl" data-id="element-266">
          <div className="text-5xl font-bold text-slate-900 mb-2" data-id="element-267">
            {averageRating.toFixed(1)}
          </div>
          <div className="flex text-yellow-400 mb-2" data-id="element-268">
            {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-5 h-5 ${star <= Math.round(averageRating) ? 'fill-current' : 'text-slate-300'}`} data-id="element-269" />)}
          </div>
          <p className="text-slate-500 text-sm" data-id="element-270">
            Based on {totalReviews} reviews
          </p>
        </div>

        {/* Distribution */}
        <div className="md:col-span-8 flex flex-col justify-center space-y-3" data-id="element-271">
          {distribution.map(({
          star,
          percentage
        }) => <div key={star} className="flex items-center gap-4" data-id="element-272">
              <div className="flex items-center gap-1 w-12 text-sm text-slate-600 font-medium" data-id="element-273">
                {star}{' '}
                <StarIcon className="w-3 h-3 fill-current text-yellow-400" data-id="element-274" />
              </div>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-275">
                <div className="h-full bg-yellow-400 rounded-full" style={{
              width: `${percentage}%`
            }} data-id="element-276" />
              </div>
              <div className="w-10 text-right text-sm text-slate-500" data-id="element-277">
                {percentage}%
              </div>
            </div>)}
        </div>
      </div>

      {/* Write a review */}
      <div className="mb-10 border-b border-slate-200 pb-10" data-id="element-278">
        {!showForm ? <div className="flex justify-between items-center" data-id="element-279">
            <div data-id="element-280">
              <h4 className="font-semibold text-slate-900" data-id="element-281">
                Share your thoughts
              </h4>
              <p className="text-sm text-slate-500" data-id="element-282">
                If you've used this product, share your thoughts with other
                customers
              </p>
            </div>
            <button onClick={() => setShowForm(true)} className="px-6 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-283">
              Write a Review
            </button>
          </div> : <form onSubmit={handleSubmit} className="bg-slate-50 p-6 rounded-xl animate-in fade-in slide-in-from-top-4" data-id="element-284">
            <h4 className="font-semibold text-slate-900 mb-4" data-id="element-285">
              Write a Review
            </h4>

            <div className="mb-4" data-id="element-286">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-287">
                Overall Rating
              </label>
              <div className="flex gap-2" data-id="element-288">
                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRating(star)} className="focus:outline-none" data-id="element-289">
                    <StarIcon className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} data-id="element-290" />
                  </button>)}
              </div>
            </div>

            <div className="mb-4" data-id="element-291">
              <label htmlFor="comment" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-292">
                Your Review
              </label>
              <textarea id="comment" rows={4} required value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none" placeholder="What did you like or dislike? What did you use this product for?" data-id="element-293" />
            </div>

            <div className="flex gap-3" data-id="element-294">
              <button type="submit" className="px-6 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 transition-colors" data-id="element-295">
                Submit Review
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors" data-id="element-296">
                Cancel
              </button>
            </div>
          </form>}
      </div>

      {/* Review List */}
      <div className="space-y-8" data-id="element-297">
        {reviews.length > 0 ? reviews.map(review => <div key={review.id} className="flex gap-4" data-id="element-298">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0" data-id="element-299">
                <UserIcon className="w-5 h-5 text-slate-500" data-id="element-300" />
              </div>
              <div data-id="element-301">
                <div className="flex items-center gap-2 mb-1" data-id="element-302">
                  <span className="font-medium text-slate-900" data-id="element-303">
                    {review.userName}
                  </span>
                  <span className="text-sm text-slate-500" data-id="element-304">
                    •{' '}
                    {new Date(review.date).toLocaleDateString('en-IN', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
                  </span>
                </div>
                <div className="flex text-yellow-400 mb-2" data-id="element-305">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-current' : 'text-slate-300'}`} data-id="element-306" />)}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed" data-id="element-307">
                  {review.comment}
                </p>
              </div>
            </div>) : <p className="text-slate-500 italic" data-id="element-308">
            No reviews yet. Be the first to review this product!
          </p>}
      </div>
    </div>;
}